import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ClockIcon, PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', error:'#EF4444', warning:'#F59E0B', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };

function FillBlankQuestion({ q, answer, onChange }) {
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ fontSize:15, fontWeight:600, color:C.navy, marginBottom:12, lineHeight:1.6 }}>
        {q.question}
      </div>
      <input
        type="text"
        value={answer || ''}
        onChange={e => onChange(String(q.id), e.target.value)}
        placeholder="Type your answer..."
        style={{
          width:'100%', padding:'12px 16px', border:`2px solid ${answer ? C.teal : C.border}`,
          borderRadius:10, fontSize:15, outline:'none', boxSizing:'border-box',
          transition:'border-color 0.2s', background:'#fff'
        }}
      />
    </div>
  );
}

function MultipleChoiceQuestion({ q, answer, onChange }) {
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ fontSize:15, fontWeight:600, color:C.navy, marginBottom:14, lineHeight:1.6 }}>
        {q.question}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {(q.options || []).map((opt, i) => {
          const selected = answer === opt;
          return (
            <button
              key={i}
              onClick={() => onChange(String(q.id), opt)}
              style={{
                padding:'12px 18px', borderRadius:10, textAlign:'left', fontSize:14,
                border:`2px solid ${selected ? C.teal : C.border}`,
                background: selected ? '#E0F7FD' : '#fff',
                color: selected ? '#005F73' : C.navy,
                fontWeight: selected ? 700 : 400,
                cursor:'pointer', transition:'all 0.15s'
              }}>
              <span style={{ fontWeight:700, marginRight:10, color:C.grey }}>{String.fromCharCode(65+i)}.</span>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WritingQuestion({ q, answer, onChange }) {
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ fontSize:15, fontWeight:600, color:C.navy, marginBottom:12, lineHeight:1.6 }}>
        {q.question}
      </div>
      <div style={{ marginBottom:8, fontSize:12, color:C.grey, display:'flex', alignItems:'center', gap:6 }}>
        <SparklesIcon style={{ width:14, height:14 }} />
        Your tutor will review this written answer in class
      </div>
      <textarea
        value={answer || ''}
        onChange={e => onChange(String(q.id), e.target.value)}
        rows={5}
        placeholder="Write your answer here..."
        style={{
          width:'100%', padding:'12px 16px', border:`2px solid ${answer ? C.teal : C.border}`,
          borderRadius:10, fontSize:14, resize:'vertical', outline:'none',
          boxSizing:'border-box', fontFamily:'inherit', lineHeight:1.6
        }}
      />
    </div>
  );
}

export default function HomeworkDo() {
  const { hwId } = useParams();
  const navigate = useNavigate();
  const [hw, setHw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [current, setCurrent] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const startTime = useRef(Date.now());

  useEffect(() => {
    api.get(`/api/student/homework/${hwId}`)
      .then(r => {
        const h = r.data.homework;
        if (h.submission) {
          // Already submitted — show result directly
          setResult({
            score: h.submission.score,
            max_score: h.max_score,
            percentage: h.submission.percentage,
            ai_feedback: h.submission.ai_feedback,
            tutor_feedback: h.submission.tutor_feedback,
            results: [],
          });
        }
        setHw(h);
      })
      .catch(() => navigate('/homework'))
      .finally(() => setLoading(false));
  }, [hwId, navigate]);

  const setAnswer = (qid, val) => setAnswers(prev => ({ ...prev, [qid]: val }));

  const submit = async () => {
    const unanswered = (hw.questions || []).filter(q => q.type !== 'writing' && !answers[String(q.id)]);
    if (unanswered.length > 0) {
      if (!window.confirm(`You have ${unanswered.length} unanswered question(s). Submit anyway?`)) return;
    }
    setSubmitting(true);
    const timeSpent = Math.round((Date.now() - startTime.current) / 60000);
    try {
      const r = await api.post(`/api/student/homework/${hwId}/submit`, { answers, time_spent_minutes: timeSpent });
      setResult(r.data);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Submission failed. Please try again.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ background:C.bg, minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ textAlign:'center', color:C.grey }}>
          <SparklesIcon style={{ width:32, height:32, margin:'0 auto 12px', display:'block', opacity:0.4 }} />
          Loading homework...
        </div>
      </div>
    );
  }

  if (!hw) return null;

  const questions = hw.questions || [];
  const totalAuto = questions.filter(q => q.type !== 'writing').length;
  const answered = Object.keys(answers).length;

  // ── Result screen ─────────────────────────────────────────────────────────
  if (result) {
    const pct = result.percentage || 0;
    const passed = pct >= 60;
    return (
      <div style={{ background:C.bg, minHeight:'100vh' }}>
        <header style={{ background:C.navy, color:'#fff', padding:'0 32px', height:64, display:'flex', alignItems:'center', gap:16 }}>
          <button onClick={() => navigate('/homework')} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:14 }}>← Back</button>
          <div style={{ fontWeight:800, fontSize:18 }}>Homework Complete</div>
        </header>

        <div style={{ maxWidth:600, margin:'40px auto', padding:'0 24px' }}>
          <motion.div initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }} style={{
            background:'#fff', borderRadius:16, padding:32, textAlign:'center', marginBottom:20,
            boxShadow:'0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize:56, marginBottom:12 }}>{pct >= 80 ? '🏆' : pct >= 60 ? '🎉' : '💪'}</div>
            <h2 style={{ margin:'0 0 8px', fontSize:26, fontWeight:800, color:C.navy }}>
              {result.score}/{result.max_score}
            </h2>
            <div style={{ fontSize:18, fontWeight:700, color: passed ? C.success : C.warning, marginBottom:16 }}>
              {pct}% — {pct >= 80 ? 'Excellent!' : pct >= 60 ? 'Good work!' : 'Keep practising!'}
            </div>

            {result.ai_feedback && (
              <div style={{ background:'#F0F7FA', borderRadius:10, padding:'16px 20px', textAlign:'left', marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.teal, marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
                  <SparklesIcon style={{ width:15, height:15 }} />
                  AI Feedback
                </div>
                <p style={{ margin:0, fontSize:14, color:C.navy, lineHeight:1.6 }}>{result.ai_feedback}</p>
              </div>
            )}

            {result.tutor_feedback && (
              <div style={{ background:'#F5F3FF', borderRadius:10, padding:'16px 20px', textAlign:'left', marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:13, color:'#8B5CF6', marginBottom:6 }}>⭐ Tutor Feedback</div>
                <p style={{ margin:0, fontSize:14, color:C.navy, lineHeight:1.6 }}>{result.tutor_feedback}</p>
              </div>
            )}

            <div style={{ padding:'12px 16px', background:'#ECFDF5', borderRadius:8, fontSize:13, color:'#065F46', marginBottom:20 }}>
              📋 Your tutor will review this before your next class
            </div>

            <button
              onClick={() => navigate('/homework')}
              style={{ width:'100%', padding:'14px', background:C.navy, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:15, cursor:'pointer' }}>
              Back to Homework Hub
            </button>
          </motion.div>

          {/* Per-question results */}
          {result.results && result.results.filter(r => r.auto_marked).length > 0 && (
            <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, color:C.navy }}>Answers breakdown</h3>
              {result.results.filter(r => r.auto_marked).map((r, i) => (
                <div key={i} style={{
                  padding:'12px 14px', marginBottom:8, borderRadius:8,
                  background: r.is_correct ? '#ECFDF5' : '#FEF2F2',
                  borderLeft:`3px solid ${r.is_correct ? C.success : C.error}`
                }}>
                  <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Q{r.id}: {r.question}</div>
                  <div style={{ display:'flex', gap:16, fontSize:13 }}>
                    <span style={{ color: r.is_correct ? C.success : C.error }}>
                      {r.is_correct ? '✅' : '❌'} Your answer: <strong>{r.student_answer || '(blank)'}</strong>
                    </span>
                    {!r.is_correct && (
                      <span style={{ color:C.grey }}>Correct: <strong>{r.correct_answer}</strong></span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Doing homework ────────────────────────────────────────────────────────
  const progress = Math.round((answered / questions.length) * 100);
  const currentQ = questions[current];

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      <header style={{ background:C.navy, color:'#fff', padding:'0 32px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <button onClick={() => navigate('/homework')} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:14 }}>← Back</button>
          <div style={{ fontWeight:700, fontSize:15 }}>{hw.title}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ fontSize:13, opacity:0.7 }}>
            {answered}/{questions.length} answered
          </div>
          <button onClick={() => setShowAll(!showAll)} style={{
            background:'rgba(255,255,255,0.1)', border:'none', color:'#fff',
            padding:'6px 14px', borderRadius:8, cursor:'pointer', fontSize:12
          }}>
            {showAll ? 'Focus Mode' : 'Show All'}
          </button>
        </div>
      </header>

      {/* Progress bar */}
      <div style={{ height:4, background:'rgba(0,180,216,0.2)' }}>
        <div style={{ height:'100%', background:C.teal, width:`${progress}%`, transition:'width 0.3s' }} />
      </div>

      <div style={{ maxWidth:700, margin:'32px auto', padding:'0 24px' }}>
        {/* Instructions */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, marginBottom:20, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
            <div style={{ fontSize:20 }}>📋</div>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:C.navy, marginBottom:4 }}>{hw.instructions}</div>
              <div style={{ fontSize:12, color:C.grey }}>
                {questions.length} questions · Est. 15–20 min · Submit before your next class
              </div>
            </div>
          </div>
        </div>

        {showAll ? (
          // All questions at once
          <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
            {questions.map((q, idx) => (
              <div key={q.id} style={{ marginBottom: idx < questions.length-1 ? 28 : 0, paddingBottom: idx < questions.length-1 ? 28 : 0, borderBottom: idx < questions.length-1 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ fontSize:12, color:C.teal, fontWeight:700, marginBottom:10 }}>
                  Question {idx+1} of {questions.length} · {q.type === 'writing' ? '✍️ Writing' : q.type === 'multiple_choice' ? '🔘 Multiple choice' : '📝 Fill in the blank'}
                </div>
                {q.type === 'multiple_choice' ? (
                  <MultipleChoiceQuestion q={q} answer={answers[String(q.id)]} onChange={setAnswer} />
                ) : q.type === 'writing' ? (
                  <WritingQuestion q={q} answer={answers[String(q.id)]} onChange={setAnswer} />
                ) : (
                  <FillBlankQuestion q={q} answer={answers[String(q.id)]} onChange={setAnswer} />
                )}
              </div>
            ))}

            <button
              onClick={submit}
              disabled={submitting}
              style={{ width:'100%', marginTop:24, padding:'16px', background: submitting ? C.grey : C.navy, color:'#fff', border:'none', borderRadius:12, fontWeight:800, fontSize:16, cursor: submitting ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
              <PaperAirplaneIcon style={{ width:20, height:20 }} />
              {submitting ? 'Submitting...' : 'Submit Homework'}
            </button>
          </div>
        ) : (
          // One question at a time
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity:0, x:30 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-30 }}
              transition={{ duration:0.2 }}
              style={{ background:'#fff', borderRadius:12, padding:28, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div style={{ fontSize:13, color:C.teal, fontWeight:700 }}>
                  Question {current+1} of {questions.length}
                </div>
                <div style={{ fontSize:12, color:C.grey }}>
                  {currentQ.type === 'writing' ? '✍️ Writing task' : currentQ.type === 'multiple_choice' ? '🔘 Multiple choice' : '📝 Fill in the blank'}
                </div>
              </div>

              {currentQ.type === 'multiple_choice' ? (
                <MultipleChoiceQuestion q={currentQ} answer={answers[String(currentQ.id)]} onChange={setAnswer} />
              ) : currentQ.type === 'writing' ? (
                <WritingQuestion q={currentQ} answer={answers[String(currentQ.id)]} onChange={setAnswer} />
              ) : (
                <FillBlankQuestion q={currentQ} answer={answers[String(currentQ.id)]} onChange={setAnswer} />
              )}

              {/* Navigation */}
              <div style={{ display:'flex', gap:12, marginTop:24 }}>
                {current > 0 && (
                  <button onClick={() => setCurrent(c => c-1)} style={{ flex:1, padding:'12px', border:`2px solid ${C.border}`, background:'#fff', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer', color:C.grey }}>
                    ← Previous
                  </button>
                )}
                {current < questions.length-1 ? (
                  <button onClick={() => setCurrent(c => c+1)} style={{ flex:2, padding:'12px', background:C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                    Next Question →
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={submitting}
                    style={{ flex:2, padding:'12px', background: submitting ? C.grey : C.navy, color:'#fff', border:'none', borderRadius:10, fontWeight:800, fontSize:14, cursor: submitting ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    <PaperAirplaneIcon style={{ width:18, height:18 }} />
                    {submitting ? 'Submitting...' : 'Submit Homework'}
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Question dots */}
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:16, justifyContent:'center' }}>
          {questions.map((q, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              style={{
                width:28, height:28, borderRadius:'50%', border:'none', cursor:'pointer', fontSize:11, fontWeight:700,
                background: answers[String(q.id)] ? C.teal : i === current ? C.navy : C.border,
                color: (answers[String(q.id)] || i === current) ? '#fff' : C.grey,
              }}>
              {i+1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
